import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "../styles/AdminLeavesPage.css";
import "../styles/AdminTeamLeavesPage.css";
import "../styles/AdminMyTeamPage.css";
import { useI18n } from "../providers/I18nProvider";
import { apiClient } from "../services/apiClient";
import { TabBar } from "./ListToolbar";

const toDateKey = (value) => (value ? String(value).slice(0, 10) : "—");

const normalizeStatus = (value) => String(value || "").trim().toLowerCase();

const parseDay = (v) => {
  const d = v ? new Date(`${String(v).slice(0, 10)}T00:00:00`) : null;
  return d && !Number.isNaN(d.getTime()) ? d : null;
};

const TeamLeavesPanel = ({ days = 30, limit = 10 }) => {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("upcoming"); // current | upcoming
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiClient.get("/leaves/admin");
      const all = Array.isArray(res.data) ? res.data : [];
      const maxDays = Math.max(1, Math.min(60, Number(days) || 30));
      const maxItems = Math.max(1, Math.min(50, Number(limit) || 10));

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const end = new Date(today.getTime());
      end.setDate(end.getDate() + maxDays);

      // Süren (bugün izinli) ve önümüzdeki maxDays içinde başlayan izinler
      const filtered = all
        .filter((l) => {
          const status = normalizeStatus(l.status);
          if (status !== "beklemede" && status !== "onaylandı") return false;
          const start = parseDay(l.start_date);
          if (!start) return false;
          const finish = parseDay(l.end_date) || start;
          return finish >= today && start < end;
        })
        .sort((a, b) => String(a.start_date || "").localeCompare(String(b.start_date || "")));

      setItems(filtered.slice(0, Math.max(maxItems, 50)));
    } catch (err) {
      setItems([]);
      setError(err.response?.data?.msg || err.message || t("common.genericError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { currentItems, upcomingItems } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const cur = [];
    const up = [];
    items.forEach((l) => {
      const start = parseDay(l.start_date);
      if (start && start <= today) cur.push(l);
      else up.push(l);
    });
    const maxItems = Math.max(1, Math.min(50, Number(limit) || 10));
    return { currentItems: cur.slice(0, maxItems), upcomingItems: up.slice(0, maxItems) };
  }, [items, limit]);
  const visible = tab === "current" ? currentItems : upcomingItems;

  const emptyText = useMemo(() => {
    const s = t("teamLeaves.empty");
    return s === "teamLeaves.empty" ? "Gösterilecek izin bulunamadı." : s;
  }, [t]);

  if (loading) return <div className="my-team-page embedded">{t("common.loading")}</div>;
  if (error) return <div className="my-team-page embedded error">{t("common.error")}: {error}</div>;

  return (
    <div className="my-team-page embedded">
      <div className="card">
        <div className="card-head">
          <h3>{t("teamLeaves.title")}</h3>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="ghost" type="button" onClick={load}>
              {t("teamLeaves.refresh")}
            </button>
            <Link className="ghost" to="/admin/team-leaves">
              {t("teamLeaves.title")}
            </Link>
          </div>
        </div>

        <TabBar
          ariaLabel={t("teamLeaves.title")}
          value={tab}
          onChange={setTab}
          tabs={[
            { key: "current", label: t("teamLeavesPanel.current"), count: currentItems.length, attention: true },
            { key: "upcoming", label: t("teamLeavesPanel.upcoming"), count: upcomingItems.length },
          ]}
        />

        {visible.length === 0 ? (
          <p className="muted">{emptyText}</p>
        ) : (
          <div className="table-wrap">
            <table className="admin-leaves-table">
              <thead>
                <tr>
                  <th>{t("teamLeaves.columns.person")}</th>
                  <th>{t("teamLeaves.columns.type")}</th>
                  <th>{t("teamLeaves.columns.start")}</th>
                  <th>{t("teamLeaves.columns.end")}</th>
                  <th>{t("teamLeaves.columns.status")}</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((l) => (
                  <tr key={l.id}>
                    <td className="cell-strong">
                      {l.user_name} {l.user_surname} {l.user_username ? `(${l.user_username})` : ""}
                    </td>
                    <td>
                      {l.leave_sub_type_name
                        ? `${l.leave_type_name} (${l.leave_sub_type_name})`
                        : l.leave_type_name}
                    </td>
                    <td className="mono">{toDateKey(l.start_date)}</td>
                    <td className="mono">{toDateKey(l.end_date)}</td>
                    <td>{l.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamLeavesPanel;

